import { FilteredNetSuiteTable } from '@/components/FilteredNetSuiteTable';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { LocationSelector } from '@/components/LocationSelector';
import { useLocationContext } from '@/contexts/LocationContext';
import { LogOutIcon, Home, Package, Scissors, Box, PenTool, Users, Grid } from 'lucide-react';
import { MobileMenu } from '@/components/layout/MobileMenu';
import { UserManagement } from '@/components/UserManagement';
import { OverviewView } from './OverviewView';
import { UpdateLog } from './UpdateLog';
import { DefaultDashboard } from './DefaultDashboard';
import { UserRole } from '@/lib/types';
import { useV2 } from '@/contexts/V2Context';
import { V2Shell } from './v2/V2Shell';
import { V2Toggle } from './v2/V2Toggle';

const allNavItems = [
  { path: '/dashboard', label: 'All Orders', icon: Home, roles: [UserRole.MANAGER, UserRole.ADMIN] },
  { path: '/dashboard/overview', label: 'Overview', icon: Grid, roles: [UserRole.MANAGER, UserRole.ADMIN, UserRole.KANBAN] },
  { path: '/dashboard/kitting', label: 'Kitting', icon: Package, roles: [UserRole.KITTING] },
  { path: '/dashboard/fabric', label: 'Fabric', icon: Scissors, roles: [UserRole.FABRIC] },
  { path: '/dashboard/metal', label: 'Metal', icon: PenTool, roles: [UserRole.POWDER] },
  { path: '/dashboard/assembly', label: 'Assembly', icon: Box, roles: [UserRole.ASSEMBLY] },
  { path: '/dashboard/users', label: 'Users', icon: Users, roles: [UserRole.USER_MANAGEMENT, UserRole.ADMIN] },
];

export function Dashboard() {
  const { logout, currentUser } = useAuth();
  const { selectedLocation } = useLocationContext();
  const { enabled: v2Enabled } = useV2();

  if (v2Enabled) return <V2Shell />;

  const getBackgroundGradient = () => {
    if (!selectedLocation) return 'from-slate-600 via-slate-700 to-slate-800';

    const [_, state] = selectedLocation.split(', ');
    switch (state) {
      case 'TX':
        return 'from-red-600 via-red-700 to-orange-800';
      case 'WA':
        return 'from-emerald-600 via-emerald-700 to-teal-800';
      case 'USA':
        return 'from-yellow-400 via-yellow-500 to-orange-400';
      case 'CA':
        return 'from-purple-600 via-purple-700 to-pink-800';
      case 'FL':
        return 'from-cyan-600 via-cyan-700 to-blue-800';
      case 'NY':
        return 'from-slate-700 via-gray-800 to-slate-900';
      case 'IL':
        return 'from-amber-600 via-yellow-700 to-orange-800';
      case 'PA':
        return 'from-rose-600 via-rose-700 to-red-800';
      case 'OH':
        return 'from-green-600 via-green-700 to-emerald-800';
      case 'GA':
        return 'from-orange-600 via-red-700 to-pink-800';
      case 'NC':
        return 'from-teal-600 via-cyan-700 to-blue-800';
      case 'MI':
        return 'from-violet-600 via-purple-700 to-indigo-800';
      case 'NJ':
        return 'from-indigo-600 via-blue-700 to-cyan-800';
      case 'VA':
        return 'from-pink-600 via-rose-700 to-red-800';
      case 'TN':
        return 'from-lime-600 via-green-700 to-emerald-800';
      case 'IN':
        return 'from-yellow-600 via-amber-700 to-orange-800';
      case 'AZ':
        return 'from-orange-600 via-amber-700 to-red-800';
      case 'MA':
        return 'from-blue-700 via-indigo-800 to-purple-900';
      case 'WI':
        return 'from-green-700 via-teal-800 to-cyan-900';
      case 'MN':
        return 'from-blue-600 via-purple-700 to-indigo-800';
      case 'CO':
        return 'from-sky-600 via-blue-700 to-indigo-800';
      case 'AL':
        return 'from-red-600 via-pink-700 to-rose-800';
      case 'LA':
        return 'from-purple-700 via-indigo-800 to-blue-900';
      case 'KY':
        return 'from-emerald-700 via-green-800 to-teal-900';
      case 'OR':
        return 'from-teal-600 via-emerald-700 to-green-800';
      case 'OK':
        return 'from-orange-700 via-red-800 to-pink-900';
      case 'CT':
        return 'from-blue-500 via-cyan-600 to-teal-700';
      case 'UT':
        return 'from-slate-600 via-gray-700 to-zinc-800';
      case 'NV':
        return 'from-yellow-700 via-orange-800 to-red-900';
      case 'AR':
        return 'from-red-700 via-rose-800 to-pink-900';
      case 'MS':
        return 'from-indigo-700 via-purple-800 to-violet-900';
      case 'KS':
        return 'from-amber-700 via-yellow-800 to-orange-900';
      case 'NM':
        return 'from-orange-500 via-red-600 to-rose-700';
      case 'NE':
        return 'from-blue-400 via-blue-500 to-indigo-600';
      case 'WV':
        return 'from-green-500 via-emerald-600 to-teal-700';
      case 'ID':
        return 'from-purple-500 via-indigo-600 to-blue-700';
      case 'HI':
        return 'from-cyan-500 via-teal-600 to-blue-700';
      case 'ME':
        return 'from-blue-700 via-slate-800 to-gray-900';
      case 'NH':
        return 'from-green-600 via-blue-700 to-indigo-800';
      case 'RI':
        return 'from-blue-600 via-indigo-700 to-purple-800';
      case 'MT':
        return 'from-slate-500 via-gray-600 to-zinc-700';
      case 'DE':
        return 'from-blue-500 via-indigo-600 to-purple-700';
      case 'SD':
        return 'from-yellow-500 via-orange-600 to-red-700';
      case 'ND':
        return 'from-amber-500 via-yellow-600 to-orange-700';
      case 'AK':
        return 'from-cyan-700 via-blue-800 to-indigo-900';
      case 'VT':
        return 'from-green-700 via-emerald-800 to-teal-900';
      case 'WY':
        return 'from-orange-400 via-amber-500 to-yellow-600';
      case 'DC':
        return 'from-red-700 via-pink-800 to-rose-900';
      default:
        return 'from-gray-600 via-slate-700 to-zinc-800';
    }
  };

  const getTextColor = () => {
    if (!selectedLocation) return 'text-white';
    return 'text-white';
  };

  // Check if user has manager role
  const hasManagerRole = currentUser?.roles.some(
    role => role === UserRole.MANAGER || role === UserRole.ADMIN
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Lightweight dotted background pattern */}
      <div className="fixed inset-0 opacity-[0.08] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='30' height='30' viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='15' cy='15' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-white/10 shadow-lg">
        <div className={`bg-gradient-to-r ${getBackgroundGradient()} relative overflow-hidden`}>
          {/* Simplified animated background elements */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-4 right-20 w-24 h-24 bg-white rounded-full blur-2xl animate-float" style={{ animationDelay: '0s' }} />
            <div className="absolute bottom-2 right-60 w-16 h-16 bg-white rounded-full blur-xl animate-float" style={{ animationDelay: '3s' }} />
          </div>
          
          <div className='max-w-[1400px] mx-auto px-4 md:px-6 relative z-10'>
            <div className='flex justify-between items-center h-20'>
              <div className='flex items-center gap-4'>
                <div className="relative group">
                  <div className="absolute inset-0 bg-white/15 rounded-2xl blur-md group-hover:blur-lg transition-all duration-200" />
                  <div className="relative bg-white/20 rounded-2xl p-2 border border-white/20 group-hover:scale-105 group-hover:shadow-lg transition-all duration-200">
                    <img src='/android-chrome-512x512.png' alt='Templar Logo' className='w-10 h-10 drop-shadow-lg' />
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full shadow-md animate-pulse" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h1 className={`text-2xl font-bold ${getTextColor()} tracking-tight drop-shadow-lg`}>
                    Production Panel
                  </h1>
                  <p className={`text-sm ${getTextColor()} opacity-90 hidden sm:block font-medium drop-shadow-sm`}>
                    Templar Screens Manufacturing
                  </p>
                </div>
              </div>
              <div className='hidden md:flex items-center gap-4'>
                <UpdateLog />
                <div className="relative">
                  <div className="absolute inset-0 bg-white/10 rounded-xl blur-sm" />
                  <div className="relative">
                    <LocationSelector />
                  </div>
                </div>
                <V2Toggle variant="dark" />
                <Button
                  variant="outline"
                  onClick={logout}
                  className="gap-2 bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200 hover:scale-105 hover:shadow-lg font-medium"
                >
                  <LogOutIcon className='w-4 h-4' />
                  <span>Logout</span>
                </Button>
              </div>
              <div className="md:hidden">
                <MobileMenu />
              </div>
            </div>
            <Navigation />
          </div>
        </div>
      </header>

      {/* Mobile Navigation - positioned outside header */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 border-t border-white/20 md:hidden shadow-md z-50">
        <div className="flex justify-around items-center h-16 px-2">
          {allNavItems
            .filter(item => {
              if (item.roles.length === 0) return true;
              if (!currentUser?.roles) return false;
              
              // For the All Orders tab, strictly check for manager or admin role
              if (item.path === '/dashboard' && item.label === 'All Orders') {
                return currentUser.roles.some(role => 
                  role === UserRole.ADMIN || 
                  role === UserRole.MANAGER
                );
              }
              
              // For other tabs, check if user has any of the required roles
              return currentUser.roles.some(role => 
                role === UserRole.ADMIN || 
                item.roles.includes(role)
              );
            })
            .filter(item => item.path !== '/dashboard/users')
            .map(({ path, label, icon: Icon }) => (
                         <NavLink
               key={path}
               to={path}
               className={({ isActive }) =>
                 `flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 relative group
                 ${isActive 
                   ? 'text-blue-600' 
                   : 'text-slate-500 hover:text-slate-700'
                 }`
               }
               end
             >
               {({ isActive }) => (
                 <>
                   <div className={`p-1 rounded-lg transition-all duration-200 ${
                     isActive ? 'bg-blue-50 scale-110' : 'group-hover:bg-slate-50 group-hover:scale-105'
                   }`}>
                     <Icon className="w-5 h-5" />
                   </div>
                   <span className="text-xs mt-1 font-medium">{label}</span>
                   {/* Active indicator */}
                   {isActive && (
                     <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-600 rounded-b-full" />
                   )}
                 </>
               )}
             </NavLink>
          ))}
        </div>
      </div>

      <main className='pt-24 md:pt-40 px-4 md:px-6 pb-24 md:pb-8 max-w-[1400px] mx-auto animate-slide-up'>
        <Routes>
          <Route index element={hasManagerRole ? <FilteredNetSuiteTable filter='all' /> : <DefaultDashboard />} />
          <Route path='fabric' element={<FilteredNetSuiteTable filter='fabric' />} />
          <Route path='kitting' element={<FilteredNetSuiteTable filter='kitting' />} />
          <Route path='metal' element={<FilteredNetSuiteTable filter='metal' />} />
          <Route path='assembly' element={<FilteredNetSuiteTable filter='assembly' />} />
          <Route path='users' element={<UserManagement />} />
          <Route path='overview' element={<OverviewView />} />
          <Route path='*' element={<Navigate to='' replace />} />
        </Routes>
      </main>
    </div>
  );
}
